import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import {
  Sidebar as CoreSidebar,
  SidebarGroup,
} from '@backstage/core-components';
import type { IconElement } from '@backstage/frontend-plugin-api';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { Icon, type IconGlyph, iconMenu } from '@ns/plugin-ns-theme';

import type { NavEntry, NavEntryIcon, NavSlots } from '../../nav-config';
import { sidebarOptions } from '../../sidebar-options';
import { SidebarThemeProvider } from '../../theme/sidebar-theme-provider';
import { SidebarControl } from '../sidebar-control/sidebar-control';
import { SidebarLogo } from '../sidebar-logo/sidebar-logo';
import { SidebarMenu } from '../sidebar-menu/sidebar-menu';
import { SidebarNavItem } from '../sidebar-nav-item/sidebar-nav-item';
import { SidebarPinToggle } from '../sidebar-pin-toggle/sidebar-pin-toggle';
import { SidebarScrollArea } from '../sidebar-scroll-area/sidebar-scroll-area';
import { SidebarSection } from '../sidebar-section/sidebar-section';

/** @public */
export type SidebarSlots = NavSlots<IconElement, IconGlyph>;

type SidebarEntry = NavEntry<IconElement, IconGlyph>;

/** Whatever the nav item takes as its icon, because the entry's icon is passed on to it. */
type NavItemIcon = ComponentProps<typeof SidebarNavItem>['icon'];

/** @public */
export interface SidebarProps {
  /** The id of the current item. Without it, no item is marked current. */
  activeItemId?: string;
  /** The items per slot, as `useNavSlots` resolves them. */
  slots: SidebarSlots;
}

interface RenderedEntry {
  /** The icon while this entry is the current one: the glyph's `selected` variant when it has one. */
  activeIcon: NavItemIcon;
  entry: SidebarEntry;
  icon: NavItemIcon;
}

function toIconComponent(
  icon: NavEntryIcon<IconElement, IconGlyph>,
  selected: boolean,
): NavItemIcon {
  if (icon.kind === 'glyph') {
    const glyph = selected ? icon.glyph.selected ?? icon.glyph : icon.glyph;
    return function GlyphIcon() {
      return <Icon glyph={glyph} />;
    };
  }
  const { element } = icon;
  return function PageIcon() {
    return <>{element}</>;
  };
}

function toRenderedEntries(entries: readonly SidebarEntry[]): RenderedEntry[] {
  return entries.map(entry => ({
    activeIcon: toIconComponent(entry.icon, true),
    entry,
    icon: toIconComponent(entry.icon, false),
  }));
}

/**
 * The NS sidebar: core's `Sidebar` with the logo, the Menu group holding the
 * slots, and the Settings group with the pin toggle.
 *
 * @remarks
 * It places the sidebar theme overrides around core's `Sidebar` itself, so a
 * caller never has to.
 *
 * @public
 */
export function Sidebar({ activeItemId, slots }: SidebarProps) {
  const rendered = useMemo(
    () => ({
      other: {
        items: toRenderedEntries(slots.other.items),
        title: slots.other.title,
      },
      primary: toRenderedEntries(slots.primary),
      sections: slots.sections.map(section => ({
        items: toRenderedEntries(section.items),
        title: section.title,
      })),
      utility: toRenderedEntries(slots.utility),
    }),
    [slots],
  );

  const renderItems = (items: readonly RenderedEntry[]) =>
    items.map(({ activeIcon, entry, icon }) => {
      const active = entry.id === activeItemId;
      return (
        <SidebarNavItem
          key={entry.id}
          active={active}
          icon={active ? activeIcon : icon}
          text={entry.title}
          to={entry.to}
        />
      );
    });

  return (
    <SidebarThemeProvider>
      <CoreSidebar sidebarOptions={sidebarOptions}>
        <SidebarLogo />

        <SidebarGroup
          icon={<Icon aria-label="Menu" glyph={iconMenu} />}
          label="Menu"
        >
          <SidebarScrollArea
            utility={<SidebarMenu>{renderItems(rendered.utility)}</SidebarMenu>}
          >
            <SidebarMenu>
              {renderItems(rendered.primary)}
              {rendered.sections.map(section => (
                <SidebarSection key={section.title} label={section.title}>
                  {renderItems(section.items)}
                </SidebarSection>
              ))}
              {rendered.other.items.length > 0 && (
                <SidebarSection label={rendered.other.title}>
                  {renderItems(rendered.other.items)}
                </SidebarSection>
              )}
            </SidebarMenu>
          </SidebarScrollArea>
        </SidebarGroup>

        <SidebarGroup
          icon={<UserSettingsSignInAvatar />}
          label="Settings"
          to="/settings"
        >
          <SidebarControl>
            <SidebarPinToggle />
          </SidebarControl>
        </SidebarGroup>
      </CoreSidebar>
    </SidebarThemeProvider>
  );
}
